import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, fromEvent, map, merge, ReplaySubject } from "rxjs";
import { auditTime, distinctUntilChanged, pairwise, startWith, switchMap } from "rxjs/operators";
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { Logger } from "../../services/logger.service";

@Component({
    selector: 'consent',
    templateUrl: './consent.component.html',
    styleUrls: ['./consent.component.scss'],
    imports: [SafeHtmlPipe, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsentComponent implements OnInit, AfterViewInit {

  @ViewChild('scrollable') scrollable!: ElementRef<HTMLElement>;
  
  @Input() set content(content: string) {
    this.logger.debug('content', content);
    this.content$.next(content);
  };

  @Input() set sectionId(sectionId: string) {
    this.logger.debug('sectionId', sectionId);
    this.sectionId$.next(sectionId);
  }
  
  public content$ = new BehaviorSubject<string>('');

  protected sectionId$ = new BehaviorSubject<string | null>(null);
  protected initialized$ = new ReplaySubject(1);

  protected logger = new Logger('ConsentComponent');

  constructor(protected elementRef: ElementRef, 
              private destroyRef: DestroyRef) {
  }

  ngOnInit(): void {
    const element = this.elementRef.nativeElement;
    this.initialized$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => combineLatest([
        this.content$,
        merge(
          this.sectionId$,
          fromEvent(element, 'scroll').pipe(map(() => null))
        )
      ])),
      auditTime(100),
      distinctUntilChanged((v1, v2) => {
        return v1?.length == v2?.length &&
               v1?.every((item, index)=> v2[index] == item)
      }),
      startWith([null, null]),
      pairwise(),
    ).subscribe(([[prevContent, prevSectionId], [currentContent, currentSectionId]]) => {
      this.logger.debug('update', {currentContent, currentSectionId});
      setTimeout(() => {
        if (currentSectionId) {
          const target = element.querySelector(`#${currentSectionId}`);
          target?.scrollIntoView();
        } else {
          if (this.sectionId$.getValue()!=currentSectionId) {
            this.sectionId$.next(currentSectionId)
          }
          if (prevContent!=currentContent) {
            this.update();
          }
        }
      });
    })
  }

  ngAfterViewInit() {
    this.initialized$.next(void 0);
    this.initialized$.complete();
  }

  onScroll() {
    this.update();
  }

  update() {
    const element = this.scrollable?.nativeElement;
    if (element) {
      const bottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 1;
      element.classList.toggle('fade-out', !bottom);
    }
  }
}
